import React, { FC, useMemo } from 'react';
import pen from '../../../assets/images/icons/pen.svg';
import { ReactComponent as CheckInfoIcon } from '../../../assets/images/icons/box.svg';
import { ReactComponent as UserIcon } from '../../../assets/images/icons/user.svg';
import { Box, useTheme } from '@mui/material';
import { useAppTranslation } from '../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { PersonaInformation, selectPersonaInformation } from '../../../app/slices/persona.slice';
import { selectTitleText } from '../../../app/slices/metadata.slice';
import { changeStep } from '../../../app/slices/step.slice';

export const Header: FC = () => {
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const { t } = useAppTranslation();

	const persona = useAppSelector(selectPersonaInformation);
	const title = useAppSelector(selectTitleText);

	const handleGoBack = () => {
		dispatch(changeStep('PERSONA_INFORMATION_INPUT_STEP'));
	};

	return (
		<div className="content-header">
			<div className="container-personal-info">
				<div className="check-info-icon-wrapper">
					<CheckInfoIcon fill={theme.palette.primary.main} className="check-info-icon" />
				</div>
				<div className="check-info-text">{title}</div>
			</div>
			<div className="container-personal-info">
				<div className="user-icon-wrapper">
					<UserIcon className="user-icon" fill={theme.palette.primary.main} />
				</div>
				<PersonaInformationContainer persona={persona} />
				<div className="edit-data-wrapper">
					<button
						type="button"
						className="icon-button edit-pen-button"
						onClick={handleGoBack}
						aria-label={t('a11y.edit-personal-data')}
					>
						<img src={pen} alt="" aria-hidden="true" className="edit-pen" />
					</button>
				</div>
			</div>
		</div>
	);
};

const PersonaInformationContainer: FC<{ persona: PersonaInformation }> = ({ persona }) => {
	const { t } = useAppTranslation();
	const { firstName, lastName, dateOfBirth, country } = persona;

	const info = useMemo(() => {
		if (dateOfBirth && country) {
			return (
				<Box data-testid="persona-info">
					<span data-testid="date-of-birth">{dateOfBirth}</span>
					{', '}
					<span data-testid="country">{t(`countries.${country}`)}</span>
				</Box>
			);
		}

		if (country) {
			return <span data-testid="country">{t(`countries.${country}`)}</span>;
		}

		if (dateOfBirth) {
			return <span data-testid="date-of-birth">{dateOfBirth}</span>;
		}

		return null;
	}, [dateOfBirth, country, t]);

	const name = `${firstName} ${lastName}`;
	return (
		<div className="user-info-container">
			<div className="user-info-text" data-testid="persona-name">
				{name}
			</div>
			{info}
		</div>
	);
};
